import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import React from 'react';
import {
  AnalyticsProvider,
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  Grid,
  GridColumn,
  Icon,
  Image,
  Modal,
  ModalProvider,
  ModalTrigger,
  Notice,
  Skeleton,
  SkeletonItem,
  Table,
  TableCell,
  TableRow,
  Tag,
  Text,
  Tooltip,
  TooltipToggle,
} from 'telia-front-react';
import { useStores } from '../stores/RootStore';
import { FetchStatus } from '../util/FetchStatus';
import { getImageSrcset } from '../util/PathUtil';
import GaPushOnMount from './GaPushOnMount';

const VIASAT_SPORT = 'VIASATSPORT';
const VIASAT_FILM = 'VIASATFILM';
const VIASAT_FILM_SPORT = 'VIASATFILMSPORT';

const VIASAT_SPORT_CODE = 'TELIA_TV_TEEMA_VIASATSPORT_E';
const VIASAT_FILM_CODE = 'TELIA_TV_TEEMA_VIASATFILM_E';
const VIASAT_FILM_SPORT_CODE = 'TELIA_TV_TEEMA_VIASATFILMSPORT_E';

export const Go3UpsellBanner = observer(() => {
  const { translate } = React.useContext(TranslationsContext);
  const { ssoStore, go3Store } = useStores();
  const { isMobile } = useIsMobileHook();
  const appName = go3Store.existingProduct.data;

  React.useEffect(() => {
    go3Store.fetchExistingApp(ssoStore.customerId);
  }, [ssoStore.customerId]);

  if ([FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(go3Store.existingProduct.status)) {
    return (
      <Skeleton label={translate('skeleton.loading')}>
        <SkeletonItem height="100px" />
      </Skeleton>
    );
  }

  if (!appName || ![VIASAT_SPORT_CODE, VIASAT_FILM_CODE, VIASAT_FILM_SPORT_CODE].includes(appName)) {
    return null;
  }

  const fixedName = appName.substring(15, appName.length - 2);
  const isSport = [VIASAT_SPORT, VIASAT_FILM_SPORT].includes(fixedName);
  const isFilm = [VIASAT_FILM, VIASAT_FILM_SPORT].includes(fixedName);
  const isFilmSport = VIASAT_FILM_SPORT === fixedName;
  const padding = isMobile ? 'small' : 'default';
  const width = isMobile ? undefined : 4;

  const renderTableHeading = () => (
    <TableRow type="header">
      <TableCell padding={padding} width={width}></TableCell>
      <TableCell padding={padding} width={width}>
        <div>
          <Tag text={translate('go3App.upsellModal.orderedTag')} purpose="success" size="tiny" />
        </div>
        <p>
          <strong>{translate(`go3App.upsellModal.title.${appName}`)}</strong>
        </p>
        <br />
        <p className="text-light">{translate(`go3App.upsellModal.regularPrice.${appName}`)}</p>
      </TableCell>
      <TableCell padding={padding} width={width}>
        <div>
          <Tag text={translate(`go3App.upsellModal.tag.new`)} purpose="primary" size="tiny" />
        </div>
        <p>
          <strong>{translate(`go3App.upsellModal.title.app.${fixedName}`)}</strong>
        </p>
        <Tooltip
          referenceElement={
            <TooltipToggle id="go-3-app-price-tooltip">
              <strong className="text-campaign">{translate('go3App.upsellModal.price.tooltipToggle')}</strong>
            </TooltipToggle>
          }
        >
          {translate('go3App.upsellModal.price.tooltip')}
        </Tooltip>
        <p className="text-light">{translate(`go3App.upsellModal.regularPrice.app.${fixedName}`)}</p>
      </TableCell>
    </TableRow>
  );

  const renderTooltipCell = (key: string, isApp = false) => (
    <TableCell padding={padding}>
      <Tooltip
        referenceElement={
          <TooltipToggle id={`${key.replace(new RegExp('.', 'g'), '-')}${isApp ? '-app' : ''}-tooltip`}>
            <span>{translate(`${key}${isApp ? '.app' : ''}.tooltipToggle`)}</span>
          </TooltipToggle>
        }
      >
        {translate(`${key}${isApp ? '.app' : ''}.tooltip`)}
      </Tooltip>
    </TableCell>
  );

  const renderCheckCell = () => (
    <TableCell padding={padding}>
      <Icon size="xs" modifiers="#check" />
    </TableCell>
  );

  const renderTable = () => (
    <Table>
      <thead>{renderTableHeading()}</thead>
      <tbody>
        {isFilm && !(isFilmSport && isMobile) && (
          <TableRow>
            <TableCell padding={padding}>{translate('go3App.upsellModal.filmStreamHub.label')}</TableCell>
            <TableCell padding={padding}>{isFilmSport ? '' : <Icon modifiers="#check" />}</TableCell>
            {renderCheckCell()}
          </TableRow>
        )}
        {isSport && !(isFilmSport && isMobile) && (
          <TableRow>
            <TableCell padding={padding}>{translate('go3App.upsellModal.sportStreamHub.label')}</TableCell>
            <TableCell padding={padding}></TableCell>
            {renderCheckCell()}
          </TableRow>
        )}
        {isFilm && (
          <TableRow>
            <TableCell padding={padding}>{translate('go3App.upsellModal.filmStream.label')}</TableCell>
            {renderTooltipCell('go3App.upsellModal.filmStream')}
            {renderCheckCell()}
          </TableRow>
        )}
        {isSport && (
          <TableRow>
            <TableCell padding={padding}>{translate('go3App.upsellModal.sportStream.label')}</TableCell>
            {renderCheckCell()}
            {renderCheckCell()}
          </TableRow>
        )}
        {isFilm && (
          <TableRow>
            <TableCell padding={padding}>{translate('go3App.upsellModal.filmChannels.label')}</TableCell>
            {renderTooltipCell('go3App.upsellModal.filmChannels')}
            {renderTooltipCell('go3App.upsellModal.filmChannels', true)}
          </TableRow>
        )}
        {isSport && (
          <TableRow>
            <TableCell padding={padding}>{translate('go3App.upsellModal.sportChannels.label')}</TableCell>
            {renderTooltipCell('go3App.upsellModal.sportChannels')}
            {renderTooltipCell('go3App.upsellModal.sportChannels', true)}
          </TableRow>
        )}
      </tbody>
      <tbody>
        <TableRow type="header">
          <TableCell padding={padding}>
            <strong>{translate('go3App.upsellModal.digibox.title')}</strong>
          </TableCell>
          <TableCell padding={padding}></TableCell>
          <TableCell padding={padding}></TableCell>
        </TableRow>
        <TableRow>
          <TableCell padding={padding}>{translate('go3App.upsellModal.digibox.arris.label')}</TableCell>
          {renderCheckCell()}
          <TableCell padding={padding}>
            {isMobile ? (
              <span className="text-campaign">{translate('go3App.upsellModal.digibox.arris.warning')}</span>
            ) : (
              <Notice type="error" size="small">
                <span className="text-campaign">{translate('go3App.upsellModal.digibox.arris.warning')}</span>
              </Notice>
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell padding={padding}>{translate('go3App.upsellModal.digibox.android.label')}</TableCell>
          {renderCheckCell()}
          {renderCheckCell()}
        </TableRow>
      </tbody>
    </Table>
  );

  return (
    <AnalyticsProvider group="Go3 Upsell Banner">
      <GaPushOnMount data={{ eventLabel: `Show - Banner Go3 Upsell ${appName}` }} />
      <Card background="campaign-1-bg">
        <CardContent>
          <Grid verticalGutter="small" noWrap={['md']}>
            <GridColumn width={['min-xs']}>
              <Image
                width={128}
                role="presentation"
                srcset={getImageSrcset(['100', '200'], 'https://res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/icons/2_Tablets.png')}
              />
            </GridColumn>
            <GridColumn width={['max-xs']}>
              <Text>
                <h2>{translate('go3App.upsellBanner.title')}</h2>
                <p>{translate('go3App.upsellBanner.description')}</p>
                <AnalyticsTrigger
                  trigger={{
                    id: ([isOpen]) => `Link - ${isOpen ? 'open' : 'close'} Modal Go3 Upsell ${appName}`,
                    callback: 'onToggle',
                  }}
                >
                  <ModalProvider>
                    <ModalTrigger>
                      <Button link text={translate('go3App.upsellBanner.button')} iconRight="#arrow-right" />
                    </ModalTrigger>
                    <Modal isFullScreen={isMobile}>
                      <div className={isMobile ? 'text-small' : ''}>{renderTable()}</div>
                    </Modal>
                  </ModalProvider>
                </AnalyticsTrigger>
              </Text>
            </GridColumn>
          </Grid>
        </CardContent>
      </Card>
    </AnalyticsProvider>
  );
});
